import * as ExpoImagePicker from 'expo-image-picker';
import * as ExpoDocumentPicker from 'expo-document-picker';
import React from 'react';
import { StyleProp, StyleSheet, ViewStyle, Image, View } from 'react-native';
import { usePermissionGuardedAction } from '../../../hooks/useMediaPermissions';
import * as FileSystem from 'expo-file-system/legacy';
import { Platform } from 'react-native';
import { getIntl } from '../../../utils/intl';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import getBrowser from '../../../utils/getBrowser';
import { useIsE2E } from '../../../hooks/useIsE2E';
import { PromptActions, StandardPrompt } from '../Prompt';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import { colors } from '../../../utils/theme';
import Icon from '../Icon';
import Stack, { contentStackSpacing } from '../Stack';
import Text from '../Text';
import { AnimateLoadedContent } from '../Loader';
import IconButton from '../IconButton';
import Button, { ButtonProps } from '../Button';
import { getSpacing } from '../../../utils/spacing';
import ScaledImage from '../ScaledImage';
import { convertHEICToPNG, isHEICFileType } from './utils';
import { notifyBugsnag } from '../../../utils/bugsnag';
import useNotify from '../../../hooks/useNotify';

const intl = getIntl('image-picker', {
    cameraRoll: 'Choose from camera roll',
    camera: 'Take a photo',
    selectFrom: 'Select from',
    addPhoto: 'Add Photo',
    addFile: 'Add File',
});

const styles = StyleSheet.create({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
    },
    pickerContainer: {
        display: 'flex',
        alignItems: 'center',
        borderColor: colors['File-Picker-Border'],
        borderRadius: 5,
        justifyContent: 'center',
        borderWidth: 1,
        backgroundColor: colors['Secondary-Button-Press'],
        overflow: 'hidden',
    },
    defaultContainer: {
        width: 132,
        height: 81,
    },
    iconContainer: {
        height: 81,
        width: 81,
    },
    logoContainer: {
        width: 132,
        height: 81,
    },
    avatarContainer: {
        width: 131,
        height: 131,
        borderRadius: 65.5,
    },
    idCardVariant: {
        width: 246,
        height: 149,
    },
    disabledAddImageButton: {
        opacity: 0.5,
    },
    errorAddImageButton: {
        borderColor: colors['Alert'],
    },
    image: {
        borderBottomWidth: 1,
        borderLeftWidth: 1,
        borderRadius: 12,
        borderRightWidth: 1,
        borderTopWidth: 1,
        height: 60,
        width: 60,
    },
    valueDisplayContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    valueDisplayLeft: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    nameTextContainer: {
        flex: 1,
        paddingLeft: 16,
    },
});

export type ImageValue = {
    base64: string;
    name: string;
};

export type PickedImage = {
    uri: string;
    base64: string;
};

export type ImagePickerSizeVariant = 'default' | 'id-card' | 'avatar' | 'logo' | 'icon';

export type RenderImageSourcePicker = (onPick: (value: ImageSource) => void) => React.ReactElement;

export type Dimensions = { width: number; height: number };

export type ErrorMessage = string;

export type DimensionValidation = (dimensions: Dimensions) => ErrorMessage | undefined;

export type ContentTypeValidation = (mimeType: string) => ErrorMessage | undefined;

export type ImagePickerOwnProps = {
    value: ImageValue | null;
    onClear: () => void;
    onPick: (imagePath: PickedImage) => void;
    style?: StyleProp<ViewStyle>;
    title?: string;
    isLoadingImage: boolean;
    isDisabled?: boolean;
    imagePickerOptions?: Partial<ExpoImagePicker.ImagePickerOptions>;
    error?: string;
    testID?: string;
    sizeVariant?: ImagePickerSizeVariant;
    addPhotoText?: string;
    /**
     * if provided, will render a custom image source picker instead of the default prompt
     */
    renderImageSourcePicker?: RenderImageSourcePicker;
    dimensionValidation?: DimensionValidation;
    contentTypeValidation?: ContentTypeValidation;
};

export type ImagePickerProps = ImagePickerOwnProps;

// this is gross, but the only way to get a file into cypress is to use a file input and validate file based functionality on the backend
const maybeRenderE2EPicker = (args: {
    isE2E: boolean;
    testId: string;
    onPick: (val: PickedImage | PickedFile) => void;
}) => {
    if (!args.isE2E) return null;

    return React.createElement('input', {
        type: 'file',
        'data-testid': args.testId,
        style: {
            display: 'none',
        },
        onChange: (event: any) => {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const uri = e.target.result;
                const base64 = uri.split(',')[1];
                args.onPick({
                    uri,
                    base64,
                    originalName: file.name,
                });
            };
            reader.readAsDataURL(file);
        },
    });
};

export type ImageSource = 'camera' | 'cameraRoll';

type ImageSourcePromptProps = {
    title: string;
    isOpen: boolean;
    onChange: (value: ImageSource) => void;
    setIsOpen: (value: boolean) => void;
    value: ImageSource | null;
};

export const ImageSourcePrompt: React.FC<ImageSourcePromptProps> = ({ title, isOpen, onChange, setIsOpen }) => {
    return (
        <StandardPrompt title={title} isOpen={isOpen} setIsOpen={setIsOpen}>
            <PromptActions
                actions={[
                    {
                        title: intl('cameraRoll'),
                        onPress: () => {
                            onChange('cameraRoll');
                        },
                        icon: 'Image',
                        variant: 'secondary',
                    },
                    {
                        title: intl('camera'),
                        onPress: () => {
                            onChange('camera');
                        },
                        icon: 'Camera',
                        variant: 'secondary',
                    },
                ]}
            />
        </StandardPrompt>
    );
};

const ImagePicker: React.FC<ImagePickerProps> = ({
    value,
    onClear,
    onPick,
    style,
    title,
    imagePickerOptions,
    isDisabled,
    isLoadingImage,
    error,
    testID,
    sizeVariant = 'default',
    renderImageSourcePicker,
    addPhotoText,
    dimensionValidation,
    contentTypeValidation,
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const [isSelectingImageSource, setIsSelectingImageSource] = React.useState(false);
    const isMobileLayout = useIsMobileLayout();
    const browser = getBrowser(navigator.userAgent);
    const isE2E = useIsE2E();
    const notify = useNotify();

    // Safari on mobile shows a native prompt for camera access, so we don't need to show our own
    // It's possible that a user on desktop with narrow window width could see this prompt, but
    // selecting either option in the prompt will open the native file picker, so it's not a big deal
    const shouldAllowPromptingForImageSource = (isMobileLayout && browser !== 'Safari') || Platform.OS !== 'web';

    const renderError = () => (error ? <Text variant="input-error">{error}</Text> : null);

    const chooseImageHandler = async (imageMode: ImageSource) => {
        try {
            const imageFunction =
                imageMode === 'camera' ? ExpoImagePicker.launchCameraAsync : ExpoImagePicker.launchImageLibraryAsync;

            const result = await imageFunction({
                ...(imagePickerOptions || {}),
                base64: Platform.OS !== 'web', // we handle base64 conversion ourselves.  For whatever reason, having expo do it causes OOM issues
            });

            if (!result.canceled) {
                const asset = result.assets?.[0];

                if (contentTypeValidation && asset?.mimeType) {
                    const errorMessage = contentTypeValidation(asset.mimeType);
                    if (errorMessage) {
                        notify('error', errorMessage);
                        return;
                    }
                }

                if (dimensionValidation && asset?.width && asset?.height) {
                    const errorMessage = dimensionValidation({ width: asset.width, height: asset.height });
                    if (errorMessage) {
                        notify('error', errorMessage);
                        return;
                    }
                }

                if (asset) {
                    if (Platform.OS === 'web') {
                        const dataUrl = await toDataUrlWeb(asset.uri);
                        onPick({ uri: dataUrl, base64: stripDataUrlPrefix(dataUrl) });
                    } else {
                        onPick({ uri: asset.uri, base64: asset.base64! });
                    }
                }
            }
        } catch (err) {
            notifyBugsnag(err as any);
            console.error(err);
        }
    };

    const handleImageSelection = () => {
        if (shouldAllowPromptingForImageSource) {
            setIsSelectingImageSource(true);
        } else {
            chooseImageHandler('cameraRoll');
        }
    };

    const permissionGuardedImageSelection = usePermissionGuardedAction(handleImageSelection);

    const imageContainerSizeVariantStyles =
        sizeVariant === 'id-card'
            ? styles.idCardVariant
            : sizeVariant === 'avatar'
            ? styles.avatarContainer
            : sizeVariant === 'icon'
            ? styles.iconContainer
            : styles.defaultContainer;

    const renderChooseButton = () => {
        const isDisabled = isDisabledByParent;
        const renderSourcePicker = () => {
            if (!shouldAllowPromptingForImageSource) return null;

            if (renderImageSourcePicker) {
                return isSelectingImageSource
                    ? renderImageSourcePicker((value) => {
                          setIsSelectingImageSource(false);
                          chooseImageHandler(value);
                      })
                    : null;
            } else if (shouldAllowPromptingForImageSource) {
                return (
                    <ImageSourcePrompt
                        title={intl('selectFrom')}
                        isOpen={isSelectingImageSource}
                        onChange={(value) => {
                            setIsSelectingImageSource(false);
                            chooseImageHandler(value);
                        }}
                        setIsOpen={setIsSelectingImageSource}
                        value={null}
                    />
                );
            } else {
                return null;
            }
        };

        return (
            <Stack vertical fullWidth align="center">
                {renderSourcePicker()}
                {maybeRenderE2EPicker({
                    isE2E,
                    onPick,
                    testId: testID || 'image-picker',
                })}
                {renderImageSourcePicker && isSelectingImageSource ? null : (
                    <PressableDynamicOpacity isDisabled={isDisabled} onPress={permissionGuardedImageSelection}>
                        <View
                            style={[
                                styles.pickerContainer,
                                imageContainerSizeVariantStyles,
                                isDisabled ? styles.disabledAddImageButton : {},
                                error ? styles.errorAddImageButton : {},
                            ]}
                        >
                            <AnimateLoadedContent isLoading={isLoadingImage}>
                                {() => (
                                    <Stack
                                        vertical
                                        align="center"
                                        space={contentStackSpacing}
                                        fullHeight
                                        justify="center"
                                    >
                                        <Icon name="Plus" size="file-picker-icon" />
                                        <Text variant="body-1">{addPhotoText || intl('addPhoto')}</Text>
                                    </Stack>
                                )}
                            </AnimateLoadedContent>
                        </View>
                    </PressableDynamicOpacity>
                )}
            </Stack>
        );
    };

    const renderImageDisplay = () => {
        const renderImage = () => {
            const imageTestID = testID ? `${testID}-display` : 'image-picker-display';
            if (sizeVariant !== 'logo') {
                return (
                    <Image
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                        source={{
                            uri: value?.base64,
                        }}
                        resizeMode="cover"
                        testID={imageTestID}
                    />
                );
            } else {
                return (
                    <ScaledImage
                        uri={value?.base64 || null}
                        containerStyle={{
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: colors['White'],
                        }}
                        restrict="width"
                        testID={imageTestID}
                        alt="Organization Logo"
                    />
                );
            }
        };

        return (
            <>
                <View
                    style={[
                        imageContainerSizeVariantStyles,
                        {
                            position: 'relative',
                        },
                    ]}
                >
                    <View style={[styles.pickerContainer, imageContainerSizeVariantStyles]}>{renderImage()}</View>
                    <View style={{ position: 'absolute', top: getSpacing(2.5), right: getSpacing(2.5) }}>
                        <IconButton
                            onPress={onClear}
                            icon="X"
                            isDisabled={isDisabled}
                            variant={sizeVariant === 'avatar' ? 'filled' : 'invert'}
                            testID={`${testID}-clear`}
                        />
                    </View>
                </View>
            </>
        );
    };

    return (
        <View style={[styles.root, style]}>
            {title ? <Text variant={'body-1'}>{title}</Text> : null}
            {value ? renderImageDisplay() : renderChooseButton()}
            {renderError()}
        </View>
    );
};

export default React.memo(ImagePicker);

export type FileValue = {
    name: string;
    originalName?: string;
};

export type PickedFile = {
    uri: string;
    base64: string;
    originalName: string;
};

export type FilePickerOwnProps = {
    value: FileValue | null;
    onClear: () => void;
    onPick: (filePath: PickedFile) => void;
    style?: StyleProp<ViewStyle>;
    title?: string;
    isLoadingFile: boolean;
    isDisabled?: boolean;
    documentPickerOptions?: Partial<ExpoDocumentPicker.DocumentPickerOptions>;
    error?: string;
    testID?: string;
};

const stripDataUrlPrefix = (uri: string) => {
    const idx = uri.indexOf(',');
    return idx === -1 ? uri : uri.substring(idx + 1);
};

// expo-image-picker / expo-document-picker (SDK 54+) return blob: URLs on web instead of data: URLs.
// Downstream code (processSystemFile) parses content type out of a data: URL, so normalize here.
const toDataUrlWeb = async (uri: string): Promise<string> => {
    if (uri.startsWith('data:')) return uri;
    const response = await fetch(uri);
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Failed to read blob as data URL'));
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
    });
};

export type FilePickerProps = FilePickerOwnProps;

const processPickedFile = async (pickedFile: ExpoDocumentPicker.DocumentPickerAsset): Promise<PickedFile> => {
    if (pickedFile.mimeType && isHEICFileType(pickedFile.mimeType)) {
        const { base64, uri, originalName, name } = await convertHEICToPNG(
            pickedFile.uri,
            pickedFile.mimeType,
            pickedFile.name,
        );
        return {
            uri: uri,
            base64,
            originalName: originalName || name,
        };
    } else {
        if (Platform.OS === 'web') {
            const dataUrl = await toDataUrlWeb(pickedFile.uri);
            return {
                uri: dataUrl,
                base64: stripDataUrlPrefix(dataUrl),
                originalName: pickedFile.name,
            };
        }
        const base64 = await FileSystem.readAsStringAsync(pickedFile.uri, {
            encoding: FileSystem?.EncodingType?.Base64,
        });
        return {
            uri: pickedFile.uri,
            base64,
            originalName: pickedFile.name,
        };
    }
};

const handleFilePicked = ({
    onPick,
    documentPickerOptions,
}: {
    onPick: (val: PickedFile) => void;
    documentPickerOptions?: Partial<ExpoDocumentPicker.DocumentPickerOptions>;
}) => {
    const handler = async () => {
        try {
            const result = await ExpoDocumentPicker.getDocumentAsync({
                ...(documentPickerOptions || {}),
                copyToCacheDirectory: true, // make file immediately available to read base64
            });

            const asset = result.assets?.[0];
            if (!result.canceled && asset) {
                onPick(await processPickedFile(asset));
            }
        } catch (err) {
            notifyBugsnag(err as any);
            console.error(err);
        }
    };
    handler();
};

const handleFilesPicked = ({
    onPick,
    documentPickerOptions,
}: {
    onPick: (val: PickedFile[]) => void;
    documentPickerOptions?: Partial<ExpoDocumentPicker.DocumentPickerOptions>;
}) => {
    const handler = async () => {
        try {
            const result = await ExpoDocumentPicker.getDocumentAsync({
                ...(documentPickerOptions || {}),
                copyToCacheDirectory: true, // make file immediately available to read base64
                multiple: true,
            });

            if (!result.canceled) {
                const assets = (result.assets || []).filter(Boolean);
                const pickedFiles = await Promise.all(assets.map(processPickedFile));
                onPick(pickedFiles);
            }
        } catch (err) {
            notifyBugsnag(err as any);
            console.error(err);
        }
    };
    handler();
};

const FilePicker: React.FC<FilePickerProps> = ({
    value,
    onClear,
    onPick,
    style,
    title,
    documentPickerOptions,
    isDisabled,
    isLoadingFile: isLoadingImage,
    error,
    testID,
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const isE2E = useIsE2E();

    const renderError = () => (error ? <Text variant="input-error">{error}</Text> : null);

    const handleFileSelection = () => {
        handleFilePicked({ onPick, documentPickerOptions });
    };

    const permissionGuardedFileSelection = usePermissionGuardedAction(handleFileSelection);

    const renderChooseButton = () => {
        const disabled = isDisabledByParent;
        return (
            <Stack vertical>
                {maybeRenderE2EPicker({
                    isE2E,
                    onPick: onPick as (val: PickedImage | PickedFile) => void,
                    testId: testID || 'file-picker',
                })}
                <PressableDynamicOpacity isDisabled={disabled} onPress={permissionGuardedFileSelection}>
                    <View
                        style={[
                            styles.pickerContainer,
                            styles.defaultContainer,
                            disabled ? styles.disabledAddImageButton : {},
                            error ? styles.errorAddImageButton : {},
                        ]}
                    >
                        <AnimateLoadedContent isLoading={isLoadingImage}>
                            {() => (
                                <Stack
                                    vertical
                                    align="center"
                                    space={contentStackSpacing}
                                    fullHeight
                                    fullWidth
                                    justify="center"
                                >
                                    <Icon name="Plus" size="file-picker-icon" />
                                    <Text variant="body-1">{intl('addFile')}</Text>
                                </Stack>
                            )}
                        </AnimateLoadedContent>
                    </View>
                </PressableDynamicOpacity>
            </Stack>
        );
    };

    const renderFileDisplay = () => {
        return (
            <Stack vertical fullWidth>
                <Stack horizontal style={styles.valueDisplayContainer} space={2} testID="file-picker-display">
                    <View style={styles.valueDisplayLeft}>
                        <Icon name="File" size="file-picker-icon" />
                        <View style={styles.nameTextContainer}>
                            <Text variant="subtitle-2">{value?.originalName || value?.name}</Text>
                        </View>
                    </View>
                    <IconButton onPress={onClear} icon="X" isDisabled={isDisabled} variant="invert" />
                </Stack>
            </Stack>
        );
    };

    return (
        <View style={[styles.root, style]}>
            {title ? <Text variant={'body-1'}>{title}</Text> : null}
            {value ? renderFileDisplay() : renderChooseButton()}
            {renderError()}
        </View>
    );
};

export { FilePicker };

export type ButtonFilePickerOwnProps = {
    onPick: (filePath: PickedFile) => void;
    isLoadingFile: boolean;
    isDisabled?: boolean;
    documentPickerOptions?: Partial<ExpoDocumentPicker.DocumentPickerOptions>;
    testID?: string;
};

export type ButtonFilePickerProps = ButtonFilePickerOwnProps & Omit<ButtonProps, 'onPress' | 'title'>;

export const ButtonFilePicker: React.FC<ButtonFilePickerProps> = ({
    onPick,
    isLoadingFile,
    isDisabled,
    documentPickerOptions,
    testID,
    ...props
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const isE2E = useIsE2E();

    const handleFileSelection = () => {
        handleFilePicked({ onPick, documentPickerOptions });
    };

    const permissionGuardedFileSelection = usePermissionGuardedAction(handleFileSelection);

    return (
        <>
            {maybeRenderE2EPicker({
                isE2E,
                onPick: onPick as (val: PickedImage | PickedFile) => void,
                testId: testID || 'file-picker',
            })}
            <Button
                {...props}
                isDisabled={isDisabledByParent || isLoadingFile}
                onPress={permissionGuardedFileSelection}
                testID={testID || 'button-file-picker'}
                title={intl('addFile')}
            />
        </>
    );
};

export type UseFilePickerArgs = ButtonFilePickerOwnProps;

export type UseFilePickerReturnValue = {
    onPress: () => void;
    E2EInput: React.ReactElement | null;
};

export const useFilePicker = (args: UseFilePickerArgs): UseFilePickerReturnValue => {
    const isE2E = useIsE2E();

    const handleFileSelection = () => {
        handleFilePicked({ onPick: args.onPick, documentPickerOptions: args.documentPickerOptions });
    };

    const permissionGuardedFileSelection = usePermissionGuardedAction(handleFileSelection);

    return {
        onPress: permissionGuardedFileSelection,
        E2EInput: maybeRenderE2EPicker({
            isE2E,
            onPick: args.onPick as (val: PickedImage | PickedFile) => void,
            testId: args.testID || 'file-picker',
        }),
    };
};

export type UseFilesPickerArgs = Omit<ButtonFilePickerOwnProps, 'onPick' | 'isLoadingFile'> & {
    onPick: (files: PickedFile[]) => void;
    isLoadingFiles: boolean;
};
export type UseFilesPickerReturnValue = {
    onPress: () => void;
    E2EInput: React.ReactElement | null;
};

export const useFilesPicker = (args: UseFilesPickerArgs): UseFilesPickerReturnValue => {
    const isE2E = useIsE2E();

    const handleFilesSelection = () => {
        handleFilesPicked({ onPick: args.onPick, documentPickerOptions: args.documentPickerOptions });
    };

    const permissionGuardedFilesSelection = usePermissionGuardedAction(handleFilesSelection);

    return {
        onPress: permissionGuardedFilesSelection,
        E2EInput: maybeRenderE2EPicker({
            isE2E,
            onPick: (val: any) => args.onPick([val]),
            testId: args.testID || 'file-picker',
        }),
    };
};
